import { TextTooltip } from '@components'
import { INocfoPermissions } from '@root/query'
import React, { useMemo, useState } from 'react'
import { FaGlasses, FaShieldAlt } from 'react-icons/fa'
import styled from 'styled-components'

type AvatarShape = 'round' | 'square'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  src?: string
  size?: number
  name?: string
  color?: string
  hashValue?: string
  getInitials?: (name?: string, fallback?: string) => string
  forceShowInitials?: boolean
  sizeLimitOneCharacter?: number
  shape?: AvatarShape
  permissions?: INocfoPermissions
  borderRadius?: number | string
}

const DEFAULT_SIZE = 32
const SINGLE_INITIAL_THRESHOLD = 20
const DEFAULT_AVATAR_COLOR = { backgroundColor: '#F4F6FA', textColor: '#234361' }

const AVATAR_COLORS = [
  { backgroundColor: '#E0F2FE', textColor: '#0F609B' },
  { backgroundColor: '#EAF5FF', textColor: '#1070CA' },
  { backgroundColor: '#E9EDFF', textColor: '#735DD0' },
  { backgroundColor: '#FDF3FF', textColor: '#B83280' },
  { backgroundColor: '#FEF6E6', textColor: '#B54708' },
  { backgroundColor: '#F1FBF4', textColor: '#00783E' },
  { backgroundColor: '#F4F5F7', textColor: '#425A70' }
]

const defaultGetInitials = (name?: string, fallback = '?') => {
  if (!name || typeof name !== 'string') return fallback
  return name
    .replace(/\s+/, ' ')
    .split(' ')
    .slice(0, 2)
    .map(value => value && value[0]?.toUpperCase())
    .join('')
}

const calculateHashValue = (value?: string) => {
  const str = String(value ?? '')
  let hash = 0

  if (str.trim().length === 0) return hash

  for (let i = 0; i < str.length; i += 1) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash &= hash
  }

  return Math.abs(hash)
}

const getAvatarInitialsFontSize = (size: number, sizeLimitOneCharacter: number) => {
  if (size <= sizeLimitOneCharacter) {
    return Math.floor(size / 3.2)
  }

  return Math.floor(size / 3.6)
}

const getAvatarColors = (color: string | undefined, hash: number) => {
  if (color && color !== 'automatic') {
    return { backgroundColor: color, textColor: '#ffffff' }
  }

  const palette = AVATAR_COLORS[hash % AVATAR_COLORS.length]
  return palette ?? DEFAULT_AVATAR_COLOR
}

export const Avatar: React.FC<Props> = ({
  src,
  size = DEFAULT_SIZE,
  name,
  color = 'automatic',
  hashValue: propHashValue,
  getInitials: getInitialsProp = defaultGetInitials,
  forceShowInitials = false,
  sizeLimitOneCharacter = SINGLE_INITIAL_THRESHOLD,
  shape = 'round',
  permissions,
  borderRadius,
  className,
  ...rest
}) => {
  const [imageHasFailedLoading, setImageHasFailedLoading] = useState(false)

  const hash = useMemo(
    () => calculateHashValue(propHashValue || name || src || String(size)),
    [name, propHashValue, src, size]
  )

  const { backgroundColor, textColor } = useMemo(() => getAvatarColors(color, hash), [color, hash])

  const initials = useMemo(() => {
    const letters = getInitialsProp(name)
    if (size <= sizeLimitOneCharacter) {
      return letters.slice(0, 1)
    }
    return letters
  }, [getInitialsProp, name, size, sizeLimitOneCharacter])

  const fontSize = useMemo(
    () => getAvatarInitialsFontSize(size, sizeLimitOneCharacter),
    [size, sizeLimitOneCharacter]
  )

  const showInitials = forceShowInitials || !src || imageHasFailedLoading
  const isAdmin = permissions?.admin?.value || false
  // A user is read-only when they hold no write permissions, regardless of
  // whether they've been granted any of the `*_read` view permissions.
  const readOnly = permissions
    ? Object.entries(permissions).every(
        ([key, permission]) => key.endsWith('_read') || !permission.value
      )
    : false

  const { title, ...restProps } = rest

  return (
    <AvatarWrapper
      {...restProps}
      className={className}
      title={title ?? name}
      $size={size}
      $shape={shape}
      $backgroundColor={backgroundColor}
      $textColor={textColor}
      $borderRadius={borderRadius}
    >
      <AvatarContent>
        {showInitials ? (
          <Initials $fontSize={fontSize}>{initials}</Initials>
        ) : (
          <AvatarImage src={src} alt={name} onError={() => setImageHasFailedLoading(true)} />
        )}
      </AvatarContent>

      {isAdmin && (
        <TextTooltip tooltip="Käyttäjä on pääkäyttäjä">
          <UserBadge className="admin">
            <FaShieldAlt size={10} />
          </UserBadge>
        </TextTooltip>
      )}

      {readOnly && (
        <TextTooltip tooltip="Käyttäjällä on vain lukuoikeus">
          <UserBadge className="read-only">
            <FaGlasses size={10} />
          </UserBadge>
        </TextTooltip>
      )}
    </AvatarWrapper>
  )
}

const AvatarWrapper = styled.div<{
  $size: number
  $shape: AvatarShape
  $backgroundColor: string
  $textColor: string
  $borderRadius?: number | string
}>`
  position: relative;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  min-width: ${({ $size }) => $size}px;
  min-height: ${({ $size }) => $size}px;
  border-radius: ${({ $shape, $borderRadius }) => {
    if ($borderRadius !== undefined) {
      return typeof $borderRadius === 'number' ? `${$borderRadius}px` : $borderRadius
    }
    return $shape === 'round' ? '50%' : '8px'
  }};
  display: flex;
  flex-shrink: 0;
  color: ${({ $textColor }) => $textColor};
  background-color: ${({ $backgroundColor }) => $backgroundColor};
`

/* Clips the image/initials to the avatar shape while letting the
   permission badges overflow the wrapper's edge. */
const AvatarContent = styled.div`
  width: 100%;
  height: 100%;
  border-radius: inherit;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const Initials = styled.span<{ $fontSize: number }>`
  font-weight: 500;
  font-size: ${({ $fontSize }) => $fontSize}px;
  line-height: ${({ $fontSize }) => $fontSize}px;
  text-transform: uppercase;
`

const UserBadge = styled.div`
  position: absolute;
  width: 1.2rem;
  height: 1.2rem;
  border-radius: 1.2rem;
  right: -2px;
  bottom: -2px;
  border: 1px solid #fff;
  display: flex;
  justify-content: center;

  &.admin {
    background: ${({ theme }) => theme.colors.AIPurple};

    svg {
      align-self: center;
      fill: #fff !important;
    }
  }

  &.read-only {
    background: ${({ theme }) => theme.colors.primary};

    svg {
      align-self: center;
      fill: #fff !important;
    }
  }
`
